import { useTranslation } from "react-i18next"
import { format } from "date-fns"
import { UserAvatarTooltip } from "@/components/avatar-user-tooltip"
import { UserProvider } from "@/components/user-wrapper"
import { useCurrentLanguage } from "@/hooks/useCurrentLanguage"
import { useFormatPhoneNumber } from "@/hooks/useFormatPhoneNumber"
import { joinStrings } from "@/lib/joinString"
import { selectTranslationNameForLanguage } from "@/lib/select-translation-name-for-language"
import { cn } from "@/lib/utils"
import { historyEventsTableLookupsQuery$data } from "@/modules/care-provider/components/edit-care-provider/history/__generated__/historyEventsTableLookupsQuery.graphql"

type LogDetails = {
  FieldName: string
  ChangedValue: string
  OldValue: string
}

type HistoryEventsRowProps = {
  createdAt: string
  createdBy: string
  jsonPayload: string | null
  isScrollable?: boolean
  specializationTranslations: historyEventsTableLookupsQuery$data["ch_specializationTranslations"]
  specialtyTranslations: historyEventsTableLookupsQuery$data["ch_specialtyTranslations"]
  officialLanguagesTranslations: historyEventsTableLookupsQuery$data["ch_officialLanguagesLookupTranslations"]
  displayableCountries: historyEventsTableLookupsQuery$data["displayableCountries"]
}

export const HistoryEventsRow = ({
  createdAt,
  createdBy,
  jsonPayload,
  isScrollable,
  specializationTranslations,
  specialtyTranslations,
  officialLanguagesTranslations,
  displayableCountries,
}: HistoryEventsRowProps) => {
  const { t } = useTranslation()
  const currentLanguage = useCurrentLanguage()

  const logDetails: LogDetails[] = jsonPayload ? JSON.parse(jsonPayload) : []

  const phoneNumberField = logDetails.find((detail) => detail.FieldName === "PhoneNumber")
  const additionalPhoneNumberField = logDetails.find((detail) => detail.FieldName === "AdditionalPhoneNumber")
  const formattedOldPhoneNumber = useFormatPhoneNumber(phoneNumberField?.OldValue)
  const formattedNewPhoneNumber = useFormatPhoneNumber(phoneNumberField?.ChangedValue)
  const formattedOldAdditionalPhoneNumber = useFormatPhoneNumber(additionalPhoneNumberField?.OldValue)
  const formattedNewAdditionalPhoneNumber = useFormatPhoneNumber(additionalPhoneNumberField?.ChangedValue)

  const getDisplayedText = (field: string, oldValue: string, newValue: string) => {
    const text = {
      field,
      oldValue,
      newValue,
    }
    switch (field) {
      case "AdditionalAddress":
        text.field = "admin.careProviders.dialog.update.address.additionalAddress"
        break
      case "CountryCode":
        text.field = "common.country"
        text.oldValue = getDisplayedCountry(oldValue)
        text.newValue = getDisplayedCountry(newValue)
        break
      case "City":
        text.field = "common.city"
        break
      case "PostalCode":
        text.field = "common.postalCode"
        break
      case "StreetAddress":
        text.field = "common.streetAddress"
        break
      case "PhoneNumber":
        text.field = "common.phoneNumber"
        text.oldValue = formattedOldPhoneNumber || oldValue
        text.newValue = formattedNewPhoneNumber || newValue
        break
      case "AdditionalPhoneNumber":
        text.field = "patient.history.additionalPhoneNumber"
        text.oldValue = formattedOldAdditionalPhoneNumber || oldValue
        text.newValue = formattedNewAdditionalPhoneNumber || newValue
        break
      case "Email":
        text.field = "common.email"
        break
      case "Website":
        text.field = "common.website"
        break
      case "OfficialLanguageIds":
        text.field = "common.language"
        text.oldValue = getOfficialLanguageNames(oldValue)
        text.newValue = getOfficialLanguageNames(newValue)
        break
      case "SpecializationIds":
        text.field = "admin.careProviders.advancedSearch.filter.specialization"
        text.oldValue = getSpecializationNames(oldValue)
        text.newValue = getSpecializationNames(newValue)
        break
      case "SpecialtyIds":
        text.field = "admin.careProviders.advancedSearch.filter.specialty"
        text.oldValue = getSpecialtyNames(oldValue)
        text.newValue = getSpecialtyNames(newValue)
        break
      case "CreditorCodeRegister":
        text.field = "patient.history.zsrNumber"
        break
      case "IsMedgatePartnerNetworkMember":
        text.field = "admin.careProviders.dialog.update.admission.medgatePartnerNetwork"
        text.oldValue = getSwitchingDisplayedValue(oldValue)
        text.newValue = getSwitchingDisplayedValue(newValue)
        break
      case "IsWalkIn":
        text.field = "admin.careProviders.dialog.update.admission.walkIn"
        text.oldValue = getSwitchingDisplayedValue(oldValue)
        text.newValue = getSwitchingDisplayedValue(newValue)
        break
      case "IsTakesNewPatients":
        text.field = "admin.careProviders.dialog.update.admission.takesNewPatients"
        text.oldValue = getSwitchingDisplayedValue(oldValue)
        text.newValue = getSwitchingDisplayedValue(newValue)
        break
      case "IsActive":
        text.field = "patient.history.permanentlyClosed"
        text.oldValue = getSwitchingDisplayedValue(oldValue, true)
        text.newValue = getSwitchingDisplayedValue(newValue, true)
        break
      case "IsIndependentContractor":
        text.field = "patient.history.doesNotWorkIndependently"
        text.oldValue = getSwitchingDisplayedValue(oldValue)
        text.newValue = getSwitchingDisplayedValue(newValue)
        break
    }
    return {
      field: t(text.field),
      oldValue: text.oldValue || '""',
      newValue: text.newValue || '""',
    }
  }

  const getSwitchingDisplayedValue = (value: string, invert: boolean = false) => {
    switch (value?.toLowerCase()) {
      case "true":
        return t(`patient.history.${invert ? "off" : "on"}`)
      case "false":
        return t(`patient.history.${invert ? "on" : "off"}`)
      default:
        return value
    }
  }

  const getDisplayedCountry = (countryCode: string) => {
    const country = displayableCountries.find((c) => c.countryIsoCode2 === countryCode)
    return country?.countryName || countryCode
  }

  const getOfficialLanguageNames = (value: string) => {
    const officialLanguageIds = value.split(",").map(Number)

    const officialLanguageNames = officialLanguageIds
      .map((id) =>
        selectTranslationNameForLanguage(
          currentLanguage,
          officialLanguagesTranslations
            .filter((translation) => translation.officialLanguageLookupId === id)
            .map((translation) => ({ language: translation.language, name: translation.isoLanguageName })),
        ),
      )
      .filter(Boolean)

    return joinStrings(officialLanguageNames, ", ")
  }

  const getSpecializationNames = (value: string) => {
    const specializationIds = value.split(",").map(Number)

    const specializationNames = specializationIds
      .map((id) => specializationTranslations.find((item) => item.specializationId === id)?.name)
      .filter(Boolean)

    return joinStrings(specializationNames, ", ")
  }

  const getSpecialtyNames = (value: string) => {
    const specialtyIds = value.split(",").map(Number)

    const specialtyNames = specialtyIds
      .map((id) => specialtyTranslations.find((item) => item.specialtyId === id)?.name)
      .filter(Boolean)

    return joinStrings(specialtyNames, ", ")
  }

  return (
    <>
      <td
        className={cn("truncate border-b p-4 text-sm", isScrollable && "group-last-of-type:border-b-0")}
        title={format(createdAt, "dd.MM.yyyy")}>
        {format(createdAt, "dd.MM.yyyy")}
      </td>
      <td
        className={cn("truncate border-b p-4 text-sm", isScrollable && "group-last-of-type:border-b-0")}
        title={format(createdAt, "HH:mm")}>
        {format(createdAt, "HH:mm")}
      </td>
      <td className={cn("truncate border-b p-4 text-sm", isScrollable && "group-last-of-type:border-b-0")}>
        <div className="flex items-center gap-2">
          <UserProvider
            userName={createdBy}
            render={(user) => (
              <>
                <UserAvatarTooltip user={user} uniqueIdentifier={user.firstName} />
                <span className="text-muted-foreground ml-2 truncate">
                  {joinStrings([user.firstName, user.lastName], " ")}
                </span>
              </>
            )}
          />
        </div>
      </td>
      <td className={cn("truncate border-b p-4 text-sm", isScrollable && "group-last-of-type:border-b-0")}>
        <div className="flex flex-col">
          {logDetails.map(({ FieldName, OldValue, ChangedValue }) => {
            const displayedText = getDisplayedText(FieldName, OldValue, ChangedValue)
            return (
              <p
                key={FieldName}
                className="truncate text-sm"
                title={`${displayedText.field}: ${displayedText.oldValue} => ${displayedText.newValue}`}>
                <span className="text-sm font-medium">{displayedText.field}: </span>
                <span className="text-muted-foreground text-sm">{`${displayedText.oldValue} => `}</span>
                <span className="text-primary text-sm">{displayedText.newValue}</span>
              </p>
            )
          })}
        </div>
      </td>
    </>
  )
}
